import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Plus, Pencil, Trash2, Type, Loader2, GripVertical } from 'lucide-react'
import type {
  FontRule,
  CreateFontRuleRequest,
  UpdateFontRuleRequest,
  ComplianceSeverity,
} from '@/lib/api/types'
import { cn } from '@/lib/utils'

interface FontRulesPanelProps {
  fontRules: FontRule[]
  loading: boolean
  onAdd: (data: CreateFontRuleRequest) => Promise<void>
  onUpdate: (ruleId: string, data: UpdateFontRuleRequest) => Promise<void>
  onDelete: (ruleId: string) => Promise<void>
}

const FONT_FAMILIES = [
  'Arial',
  'Calibri',
  'Cambria',
  'Georgia',
  'Helvetica',
  'Inter',
  'Lato',
  'Montserrat',
  'Open Sans',
  'Poppins',
  'Roboto',
  'Source Sans Pro',
  'Times New Roman',
  'Verdana',
]

const FONT_WEIGHTS = [
  { value: 100, label: 'Thin (100)' },
  { value: 200, label: 'Extra Light (200)' },
  { value: 300, label: 'Light (300)' },
  { value: 400, label: 'Regular (400)' },
  { value: 500, label: 'Medium (500)' },
  { value: 600, label: 'Semi Bold (600)' },
  { value: 700, label: 'Bold (700)' },
  { value: 800, label: 'Extra Bold (800)' },
  { value: 900, label: 'Black (900)' },
]

const USAGE_CONTEXTS = [
  { value: 'All', label: 'All text' },
  { value: 'Headings', label: 'Headings only' },
  { value: 'Body', label: 'Body text only' },
  { value: 'Captions', label: 'Captions only' },
]

const defaultFormData: CreateFontRuleRequest = {
  fontFamily: '',
  allowedWeights: [400],
  minSize: undefined,
  maxSize: undefined,
  usageContext: 'All',
  isRequired: false,
  severity: 'Warning',
}

export function FontRulesPanel({
  fontRules,
  loading,
  onAdd,
  onUpdate,
  onDelete,
}: FontRulesPanelProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<FontRule | null>(null)
  const [deleteRule, setDeleteRule] = useState<FontRule | null>(null)
  const [formData, setFormData] = useState<CreateFontRuleRequest>(defaultFormData)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleOpenDialog = (rule?: FontRule) => {
    if (rule) {
      setEditingRule(rule)
      setFormData({
        fontFamily: rule.fontFamily,
        allowedWeights: rule.allowedWeights,
        minSize: rule.minSize,
        maxSize: rule.maxSize,
        usageContext: rule.usageContext,
        isRequired: rule.isRequired,
        severity: rule.severity,
      })
    } else {
      setEditingRule(null)
      setFormData(defaultFormData)
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingRule(null)
    setFormData(defaultFormData)
  }

  const handleSave = async () => {
    if (!formData.fontFamily) return

    setSaving(true)
    try {
      if (editingRule) {
        await onUpdate(editingRule.id, formData as UpdateFontRuleRequest)
      } else {
        await onAdd(formData)
      }
      handleCloseDialog()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteRule) return

    setDeleting(true)
    try {
      await onDelete(deleteRule.id)
      setDeleteRule(null)
    } finally {
      setDeleting(false)
    }
  }

  const toggleWeight = (weight: number) => {
    const current = formData.allowedWeights || []
    const updated = current.includes(weight)
      ? current.filter((w) => w !== weight)
      : [...current, weight].sort((a, b) => a - b)
    setFormData({ ...formData, allowedWeights: updated })
  }

  const getSeverityBadge = (severity: ComplianceSeverity) => {
    const styles = {
      Error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
      Warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
      Info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
    }
    return <Badge className={cn('text-xs', styles[severity])}>{severity}</Badge>
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading font rules...</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            Font Rules
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
            Define allowed fonts, weights, and sizes
          </p>
        </div>
        <Button size="sm" onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-1" />
          Add Font
        </Button>
      </div>

      {fontRules.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg">
          <Type className="h-8 w-8 mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No font rules defined</p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
            Add font rules to enforce typography standards
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {fontRules
            .sort((a, b) => a.order - b.order)
            .map((rule) => (
              <div
                key={rule.id}
                className="flex items-center gap-3 p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700"
              >
                <GripVertical className="h-4 w-4 text-neutral-400 shrink-0 cursor-grab" />
                <Type className="h-5 w-5 text-neutral-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="font-medium text-sm text-neutral-900 dark:text-neutral-100"
                      style={{ fontFamily: rule.fontFamily }}
                    >
                      {rule.fontFamily}
                    </span>
                    {getSeverityBadge(rule.severity)}
                    {rule.isRequired && (
                      <Badge variant="secondary" className="text-xs">
                        Required
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                    <span>
                      Weights: {rule.allowedWeights.join(', ')}
                    </span>
                    {(rule.minSize || rule.maxSize) && (
                      <span>
                        Size: {rule.minSize || 0}px - {rule.maxSize || 'any'}px
                      </span>
                    )}
                    <span className="text-neutral-400 dark:text-neutral-500">
                      {USAGE_CONTEXTS.find((c) => c.value === rule.usageContext)?.label}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => handleOpenDialog(rule)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setDeleteRule(rule)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Font Rule' : 'Add Font Rule'}</DialogTitle>
            <DialogDescription>
              Configure font family, weights, and size constraints.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Font Family</Label>
              <Select
                value={formData.fontFamily}
                onValueChange={(value) => setFormData({ ...formData, fontFamily: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select font family" />
                </SelectTrigger>
                <SelectContent>
                  {FONT_FAMILIES.map((font) => (
                    <SelectItem key={font} value={font}>
                      <span style={{ fontFamily: font }}>{font}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Allowed Weights</Label>
              <div className="flex flex-wrap gap-2">
                {FONT_WEIGHTS.map((weight) => (
                  <button
                    key={weight.value}
                    type="button"
                    onClick={() => toggleWeight(weight.value)}
                    className={cn(
                      'px-2 py-1 text-xs rounded-md border transition-colors',
                      formData.allowedWeights?.includes(weight.value)
                        ? 'bg-neutral-900 text-white border-neutral-900 dark:bg-neutral-100 dark:text-neutral-900 dark:border-neutral-100'
                        : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-400 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-600'
                    )}
                  >
                    {weight.value}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Size (px)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 10"
                  value={formData.minSize || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      minSize: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Max Size (px)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 72"
                  value={formData.maxSize || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxSize: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Usage Context</Label>
              <Select
                value={formData.usageContext}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    usageContext: value as 'All' | 'Headings' | 'Body' | 'Captions',
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USAGE_CONTEXTS.map((context) => (
                    <SelectItem key={context.value} value={context.value}>
                      {context.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Severity</Label>
              <Select
                value={formData.severity}
                onValueChange={(value) =>
                  setFormData({ ...formData, severity: value as ComplianceSeverity })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Error">Error - Block non-compliant</SelectItem>
                  <SelectItem value="Warning">Warning - Show warning</SelectItem>
                  <SelectItem value="Info">Info - Informational only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Required Font</Label>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  This font must be used in the specified context
                </p>
              </div>
              <Switch
                checked={formData.isRequired}
                onCheckedChange={(checked) => setFormData({ ...formData, isRequired: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !formData.fontFamily}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingRule ? 'Save Changes' : 'Add Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteRule} onOpenChange={(open) => !open && setDeleteRule(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Font Rule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the font rule for "{deleteRule?.fontFamily}"? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRule(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
